import React, { useEffect, useState } from 'react';
import { UserEntity } from 'src/store/userApi';
import { selectOptions } from 'src/store/optionSlice';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from 'src/ui/Button';
import { Card } from 'src/ui/Card';

interface Option {
  labels: any;
}

interface UserProfileDisplayProps {
  user: UserEntity | null;
  title: string;
  /** Extra right padding for the header row, e.g. to clear a modal close button. */
  headerPaddingRight?: number;
}

const UserProfileDisplay = ({
  user,
  title,
  headerPaddingRight,
}: UserProfileDisplayProps) => {
  const [optionsMap, setOptionsMap] = useState<Record<string, Option>>({});
  const allOptions = useSelector(selectOptions);

  useEffect(() => {
    const optionKeys = Object.keys(allOptions);
    if (optionKeys.length) {
      const _optionsMap: { [key: string]: any } = {};
      optionKeys.forEach((key: any) => {
        const field = allOptions[key]?.field;
        _optionsMap[field] = allOptions[key];
      });
      setOptionsMap(_optionsMap);
    }
  }, [allOptions]);

  const renderPhotos = () => {
    if (!user?.photos || user.photos.length === 0) {
      return (
        <Card pad>
          <span
            style={{
              color: 'var(--text-muted)',
              textAlign: 'center',
              display: 'block',
              fontSize: 'var(--fs-sm)',
            }}
          >
            No photos uploaded
          </span>
        </Card>
      );
    }

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
        }}
      >
        {user.photos.slice(0, 4).map((photo, index) => (
          <div
            key={photo.id}
            style={{
              aspectRatio: '1',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: 'var(--bg-canvas)',
            }}
          >
            <img
              src={photo.lImage?.url || photo.mImage?.url || photo.sImage?.url}
              alt={`User ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const decodeFieldValue = (fieldKey: string, value: any) => {
    if (!value && value !== 0) return '';

    if (optionsMap[fieldKey]) {
      const options = optionsMap[fieldKey].labels;
      const isBinary = options[1]?.[0] <= 2;

      if (isBinary) {
        return options
          .reduce((acc: any, cur: any) => {
            if (Number(value) & Math.pow(2, cur[0])) {
              acc.push(cur[1].cn || cur[1].en);
            }
            return acc;
          }, [])
          .join(', ');
      } else {
        const option = options.find((option: any) => option[0] === value);
        return option?.[1]?.cn || option?.[1]?.en || value;
      }
    }

    return value;
  };

  const renderProfileField = (key: string, value: any) => {
    if (!value && value !== 0) return null;
    if (typeof value === 'object' && !Array.isArray(value)) return null;

    const displayValue = decodeFieldValue(key, value);

    return (
      <div key={key} style={{ marginBottom: 8 }}>
        <div
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-secondary)',
            fontWeight: 500,
            marginBottom: 2,
          }}
        >
          {key}
        </div>
        <div
          style={{
            fontSize: 'var(--fs-sm)',
            wordBreak: 'break-word',
            color: 'var(--text)',
          }}
        >
          {displayValue}
        </div>
      </div>
    );
  };

  const renderUserHeaderInfo = () => {
    if (!user?.profile) return title;

    const name = user.profile?.name || user.email;

    const infoParts: (string | number)[] = [];

    if (user.profile?.gender) {
      infoParts.push(decodeFieldValue('gender', user.profile.gender));
    }

    if (user.profile?.age) {
      infoParts.push(user.profile.age);
    }

    const locationParts: string[] = [];
    if (user.profile?.city) {
      locationParts.push(decodeFieldValue('city', user.profile.city));
    }
    if (user.profile?.state) {
      locationParts.push(decodeFieldValue('state', user.profile.state));
    }
    if (user.profile?.country) {
      locationParts.push(decodeFieldValue('country', user.profile.country));
    }

    if (locationParts.length > 0) {
      infoParts.push(locationParts.join(', '));
    }

    return (
      <div>
        <div
          style={{
            fontWeight: 700,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text)',
          }}
        >
          {name}
        </div>
        {infoParts.length > 0 && (
          <div
            style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}
          >
            {infoParts.join(' • ')}
          </div>
        )}
      </div>
    );
  };

  const renderProfileFields = () => {
    if (!user?.profile) return null;

    const profileKeys = Object.keys(user.profile).filter(
      (key) =>
        ![
          'id',
          'userId',
          'createdAt',
          'updatedAt',
          'answers',
          'extraData',
        ].includes(key)
    );

    const answersKeys = user.profile.answers
      ? Object.keys(user.profile.answers).sort()
      : [];

    return (
      <div>
        {/* User ID - First Item */}
        <div style={{ marginBottom: 8 }}>
          <div
            style={{
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              marginBottom: 2,
            }}
          >
            User ID
          </div>
          <div
            style={{
              fontSize: 'var(--fs-sm)',
              wordBreak: 'break-word',
              color: 'var(--text)',
            }}
          >
            {user.id}
          </div>
        </div>

        {/* Basic Profile Fields */}
        {profileKeys.map((key) => renderProfileField(key, user.profile?.[key]))}

        {/* Answer Fields */}
        {answersKeys.length > 0 && (
          <>
            <hr
              style={{
                border: 'none',
                borderTop: '1px solid var(--border)',
                margin: '12px 0',
              }}
            />
            <div
              style={{
                fontSize: 'var(--fs-sm)',
                fontWeight: 700,
                color: 'var(--text)',
                marginBottom: 8,
              }}
            >
              Interview Answers
            </div>
            {answersKeys.map((key) =>
              renderProfileField(key, user.profile?.answers?.[key])
            )}
          </>
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Fixed Header */}
        <div
          style={{
            padding: 16,
            paddingRight: headerPaddingRight ?? 16,
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 'var(--fs-md)',
              color: 'var(--text-muted)',
            }}
          >
            {title}
          </span>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              color: 'var(--text-muted)',
              textAlign: 'center',
              fontSize: 'var(--fs-sm)',
            }}
          >
            No user selected
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <div
        style={{
          padding: 16,
          paddingRight: headerPaddingRight ?? 16,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          {renderUserHeaderInfo()}
          <Link to={`/user-profile/${user.id}`} target="_blank">
            <Button size="sm" variant="secondary" rightIcon="external-link">
              Edit Profile
            </Button>
          </Link>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div className="col" style={{ gap: 16 }}>
          {/* Photos */}
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 'var(--fs-sm)',
                color: 'var(--text)',
                marginBottom: 8,
              }}
            >
              Photos
            </div>
            {renderPhotos()}
          </div>

          <hr
            style={{
              border: 'none',
              borderTop: '1px solid var(--border)',
              margin: 0,
            }}
          />

          {/* Profile Fields */}
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 'var(--fs-sm)',
                color: 'var(--text)',
                marginBottom: 12,
              }}
            >
              Profile Information
            </div>
            {renderProfileFields()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileDisplay;
